import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useIntl } from 'umi';
import { LANGUAGE_EN, SYSTEM_THEME_DARK } from '@home/_config/config';
import { Card } from 'tea-component';
import RadarChart from '@/pages/dashboard/components/Chart/components/RadarChart';
import type { ChartRef } from '@/pages/dashboard/components/Chart/_config/interface';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import { dateAllFormat, getLanguage } from '@home/utils/common';
import { DEFAULT_IMG, dataToArr, versionCompare } from './utils';
import LevelSvg from '@/pages/dashboard/assets/svg/benchmark-level.svg';
import { merge } from 'lodash-es';
import { changeStudioName } from "@/pages/home/pages/studio/studioAssessment/components/StudioAssessnebtTableCard";
import './index.scss';
import { ThemeContext } from '@home/wrappers/common';
import gtdrBackgroundWhite from '@/pages/dashboard/assets/images/benchmark/gtdr-en-light.png';
import gtdrBackgroundDark from '@/pages/dashboard/assets/images/benchmark/gtdr-en-dark.png';
import gtdrBackgroundWhiteCn from '@/pages/dashboard/assets/images/benchmark/gtdr-cn-light.png';
import gtdrBackgroundDarkCn from '@/pages/dashboard/assets/images/benchmark/gtdr-cn-dark.png';
import classNames from 'classnames';
import { StudioContext } from "@/pages/home/pages/studio/context";
import { dynamicString } from '@home/utils/dynamicString';
import useFullScreen from '@home/components/FullScreenScale/_hooks/useFullScreen';
import TipContext from '@home/components/TipContext';
import { canvasOption } from '@/pages/dashboard/utils/radiusImg';
import { captureNode, CAPTURE_REMOVE_CLASS_NAME } from '@/pages/dashboard/utils/captureUtils';
import Loading from '@/pages/dashboard/components/Loadings';


const GTDRRadarChart = (props: any) => {
  const {
    className = '',
    style,
    height = 670,
    cardHeight = 642,
    data,
    subtitle,
    titleEn,
    titleCn,
    performanceData,
    performanceLoading,
    performanceError,
    benchmarkLevel,
    checkList,
    filterValue,
    isSort = true,
    gameName,
    gameCover,
    latestVersionObj,
    changeData,
    // categoryColorArr = ["rgba(94, 200, 241, 0.5)", "rgba(241, 158, 85, 0.5)", "rgba(105, 209, 156, 0.5)", "rgba(248, 215, 77, 0.5)"],
    showTitle = true,
    // categoryColorArrDark = [
    //   "rgba(94, 200, 241, 0.11)",
    //   "rgba(241, 158, 85, 0.11)",
    //   "rgba(105, 209, 156, 0.11)",
    //   "rgba(248, 215, 77, 0.11)"
    // ],
    // pieColorArr = ["rgba(94, 200, 241, 0.3)", "rgba(241, 158, 85, 0.3)", "rgba(105, 209, 156, 0.3)", "rgba(248, 215, 77, 0.3)"],
    checkBorderWidth = 0,
    isStudioChart = false,
    checkColorMap = {
      benchmark_high: "#4C526E",
      benchmark_mid: "#8790AE",
      benchmark_low: "#B8BED1",
    },
    checkColorMapDark= {
      benchmark_high: "#6A728C",
      benchmark_mid: "#9BA1B9",
      benchmark_low: "#B8BED1",
    },
    colorArrLess = ["#5E54CC", "#AD9EFF", "#E4DEFE"],
    colorArrMore = ["#3E39A4", "#5E54CC", "#AD9EFF", "#E4DEFE", "#EFEBFF", "#5e54cc5c", "#7f6ff15c", "#ad9eff5c", "#d2c8fd5c", "#efebff5c", "#1B1C59", "#292880", "#413CA6", "#7F6FF1", "#D2C8FD", "#EFEBFF"],
    pieLabelOffsetEn= [
      [-35, 45],
      [-5, -45],
      [27, -45],
      [20, 45],
    ],
    pieLabelOffsetCn= [
      [-32, 45],
      [-32, -45],
      [38, -45],
      [25, 45],
    ],
    backgroundWhite = gtdrBackgroundWhite,
    backgroundWhiteCn = gtdrBackgroundWhiteCn,
    backgroundDark = gtdrBackgroundDark,
    backgroundDarkCn = gtdrBackgroundDarkCn,
    radarStartAngle = 105,
    benchmarkConfig,
    radarRadius= ["0%", "50%"],
    radarLabelNameGap = 85,
    formattedRiskData,
    showRiskLevel,
    riskColumns,
    showDownloadBtn = false,
    showFullscreenBtn = false,
  } = props;
  const { companyInfo, currentGame } = useContext(StudioContext);

  const { commonData } = data || {};

  const { data: radarChartData = [], loading: radarChartLoaidng, errorMsg: radarChartError } = commonData || {};
  const { locale, formatMessage } = useIntl();
  const isEN = locale === LANGUAGE_EN;
  const [loadingEchart, setLoadingEchart] = useState(false);
  const [iconOption, setIconOption] = useState<any>({});
  const [metricKey, setMetricKey] = useState<any>("");
  const [selected, setSelected] = useState<any>({});
  const [isDownloading, setIsDownloading] = useState(false);

  const { themeValue } = useContext(ThemeContext);
  const benchmarkLevels = benchmarkConfig?.map((config: { benchmark_level: string; }) => config.benchmark_level) || [];
  const isDark = themeValue === SYSTEM_THEME_DARK;
  let backgroundImage;

  if (isEN) {
    backgroundImage = isDark ? backgroundDark : backgroundWhite;
  } else {
    backgroundImage = isDark ? backgroundDarkCn : backgroundWhiteCn;
  }
  const checkColor = isDark ? checkColorMapDark : checkColorMap;

  const getUniqueKey = (name: string, version: string) => `${changeStudioName(name, currentGame, companyInfo)} - ${version}`;
  // 生成圆角svg用于图例富文本
  const getIcon = async () => {
    setLoadingEchart(true);
    try {
      const _iconsData = {}; // 跟随产品名称动态生成图片
      const gameCoverArray = dataToArr(gameCover);
      for(let index=0;index<gameCoverArray.length;index++){
        const element = gameCoverArray[index];
        const _icon = await canvasOption({ game: element }, 30, 30, 8, "", false, { needCrossOrigin: true });
        _iconsData[`icon${index}`] = {
          fontSize: 14,
          lineHeight: 20,
          width: 30,
          verticalAlign: isStudioChart ? "top" : "middle",
          height: 30,
          borderRadius: 20,
          backgroundColor: {
            borderRadius: 20,
            image: _icon?.game || DEFAULT_IMG,
          }
        };
      };
      setIconOption(_iconsData);
    } catch (error) {
      console.error('Error generating icons:', error); // 添加错误日志
    } finally {
      setLoadingEchart(false); // 确保在 finally 块中设置加载状态为 false
    };
  };

  useEffect(() => {
    getIcon();
  }, [gameCover.toString()]);


  useEffect(() => {
    setSelected({});
  }, [filterValue, benchmarkLevel, isEN]);

  useEffect(() => {
    changeData('subtitle', dateAllFormat(radarChartData?.[0]?.update_date,"day") || "-");
  }, [radarChartData]);

  const { loading, error, isNodata } = useMemo(() => ({
    isNodata: performanceData?.length === 0 && radarChartData?.length === 0,
    loading: performanceLoading || radarChartLoaidng || loadingEchart,
    error: radarChartError || performanceError,
  }), [loadingEchart, performanceData?.length, performanceError, performanceLoading, radarChartData?.length, radarChartError, radarChartLoaidng]);

  const { title, category, metric, metricIdList, metricCategory, legend, legendNameMap, legendNameIconMap, versionList, priorityNames } = useMemo(() => {
    const title = getLanguage(isEN, titleEn, titleCn);
    // 分类
    const category: any = [];
    const categoryIdList: any = [];
    // 指标
    const metric: any = [];
    const metricIdList: any = [];
    // 指标分类
    const metricCategory: any = [];
    (performanceData || [])?.forEach((item: any) => {
      const categoryName = getLanguage(isEN, item?.category_en, item?.category_cn);
      const metricName = getLanguage(isEN, item?.index_en, item?.index_cn);
      if (!categoryIdList?.includes(item?.category)) {
        categoryIdList?.push(item?.category);
        category?.push(categoryName);
      }
      if (!metricIdList?.includes(item?.index)) {
        metricIdList?.push(item?.index);
        metric?.push(metricName);
        metricCategory?.push({ category: item?.category, index: item?.index, categoryName, metricName });
      }
    });
    // 图例数据处理
    const legendNameMap: any = {};
    // 图例icon映射
    const legendNameIconMap: any = {};
    // 根据check渲染的图例
    const filterCheck = checkList?.filter((item: { value: any; }) => benchmarkLevel?.includes(item?.value));
    // benchmark处理
    const performanceKeyList = filterCheck?.map((item: { key: any; nameEn: any; nameCn: any; }) => {
      const name = getLanguage(isEN, item?.nameEn, item?.nameCn);
      legendNameMap[item?.key] = name;
      legendNameIconMap[name] = {
        icon: 'performance'
      };
      return item?.key;
    });
    // 游戏版本处理
    const versionList = isSort ? filterValue?.version?.sort((a: string, b: string) => versionCompare(a, b)) : filterValue?.version;
    const versionKeyNameList = versionList?.map((item: string, index: number) => {
      const isArr = Array.isArray(gameName);
      const name = `${isArr ? gameName[index] : gameName}`; // 兼容单个游戏名称和多个游戏名称的情况
      const keyName = isStudioChart ? getUniqueKey(name, item) : `${name}-${item}`;

      legendNameMap[isStudioChart ? keyName : item] = keyName;
      legendNameIconMap[keyName] = {
        icon: `icon${isArr ? index : 0}` // 单笔数据icon需固定0
      };

      return keyName;
    });

    const legend = [...(performanceKeyList || []), ...(isStudioChart ? versionKeyNameList : versionList || [])];
    // 从 filterCheck 中提取所有可能的 name 值（中英文）
    const priorityNames = filterCheck
      .map((item: { nameEn: string; }) => item.nameEn)
      .concat(filterCheck.map((item: { nameCn: string; }) => item.nameCn));
    return {
      title,
      category,
      metric,
      metricIdList,
      metricCategory,
      legend,
      legendNameMap,
      legendNameIconMap,
      versionList,
      priorityNames,
    };
  }, [benchmarkLevel, checkList, filterValue?.version, gameName, isEN, isSort, performanceData, titleCn, titleEn]);

  const { legendData = [], indicator = [], seriesData = [], series = [{ name: 'radar', type: 'radar', data: [] }] } = useMemo(() => {
    if (loading || isNodata) return {};
    // 饼图数据
    const pieData = category?.map((item: string, index: number) => {
      // 控制不同 index 对应的 label 相对位置
      const offset = isEN? pieLabelOffsetEn[index] : pieLabelOffsetCn[index] || [0, 0];
      return {
        value: 10,
        name: item,
        itemStyle: { color: "none" },
        label: {
          show: false,
          position: 'inside',
          offset, // 控制 label 相对于扇形中心的偏移
          formatter: `{name|${item}}`,
          rich: {
            name: {
              fontSize: 12,
              color: isDark ? "#fff" : "rgba(38, 40, 50, 1)",
              fontWeight: 600
            }
          }
        },
      };
    });


    // 雷达图逆时针展示
    const indicator = metric?.map((item: string, index: number) => ({ key: metricIdList?.[index], name: item, max: 10 }))?.slice()?.reverse();
    const performanceObj = performanceData?.reduce((result: { [x: string]: any[]; }, item: { benchmark_level: string | number; }) => {
      // 按照benchmark_level分类
      if (!result[item.benchmark_level]) {
        result[item.benchmark_level] = [];
      }

      // 存入数据
      result[item.benchmark_level]?.push(item);
      return result;
    }, {});
    const radarChartObj = radarChartData?.reduce((result: any, item: any) => {
      const uniqueKey = isStudioChart ? getUniqueKey(item?.studio, item?.version) : item?.version;
      // 按照游戏版本分类
      if (!result[uniqueKey]) {
        result[uniqueKey] = [];
      }
      // 存入数据
      result[uniqueKey]?.push(item);
      return result;
    }, {});

    const valueDataObj = { ...(performanceObj || {}), ...(radarChartObj || {}) };
    const legendData = legend?.map((item) => ({ name: legendNameMap?.[item] || "", itemStyle: { opacity: 1 } }));
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let colorIndex = 0;
    // 是否选中latest版本
    const isSelectLatestVersion = versionList?.find((v: any) => v === latestVersionObj?.version);
    const seriesData = legend?.map((item) => {
      if (!checkColor?.[item]) {
        colorIndex += 1;
      }
      // 按设计要求版本数量小于4个用对应的色卡
      const colorSource = versionList?.length < 4 ? colorArrLess : colorArrMore;
      const color = checkColor?.[item] || colorSource?.[colorIndex - 1];
      // 根据indicator反转的顺序（需要反转数据对应）
      // 无数据版本会不返回，使用indicator兜底
      const value = valueDataObj?.[item]?.map((item: { value: any; }) => item?.value)?.slice()?.reverse() || indicator?.map((_item: any) => "");
      // 是否选中的第一个版本
      const isFistVersion = item === (
        isStudioChart // 过滤benchmarkLevels的key，studio图表取第一个非benchmarkLevels的版本
          ? Object.keys(legendNameMap).filter(key => !benchmarkLevels.includes(key))?.[0]
          : versionList?.[0]
      );
      // 是否latest版本展示
      const isLatestVersion = item === latestVersionObj?.version;
      // 是否展示圆点,latest版本展示或者选中的第一个版本
      const isShow = isSelectLatestVersion ? isLatestVersion : isFistVersion;
      const isBenchmark = benchmarkLevels.includes(item);
      return {
        name: legendNameMap?.[item] || "",
        value,
        lineStyle: {
          color,
          // 版本线加粗
          width: priorityNames?.includes(legendNameMap?.[item]) ? 2 : 4
        },
        itemStyle: {
          color,
          opacity: isShow ? 1 : 0,
          borderColor: '#fff',
          borderWidth: 2
        },
        symbolSize: 12,
        areaStyle: isBenchmark
          ? {
            color,
            opacity: 1
          }
          : {
            // 选中一个版本才展示面积颜色
            color: (isFistVersion && versionList.length === 1) ? "rgba(23, 9, 181, 0.7)" : 'none'
          },
        z: isShow? 1000:0,
        emphasis: {
          disabled: true
        }
      };
    });


    const series = seriesData?.map((item: any, index) => ({
      name: `radar${index}`,
      type: "radar",
      animation: false,
      z: item.z,
      data: [
        {
          ...item,
        }
      ],
      emphasis: {
        lineStyle: {
          color: item?.lineStyle?.color, // 设置 hover 时的线条颜色
        },
        itemStyle: {
          color: item?.itemStyle?.color, // 设置 hover 时的点颜色
        },
      },
      metricKey
    })) || [];

    // 维度数值低于benchmark的”middle“时，点的颜色变为红色
    const redDotSeries = seriesData
      ?.filter((item) => item?.itemStyle?.opacity === 1) // 过滤出opacity为1的项
      ?.map((item) => ({
        type: "radar",
        animation: false,
        z: 1001,
        data: [
          {
            name: item?.name,
            // 大于6的值替换为Infinity，小于等于6的值保留
            value: item?.value?.map((value: string) => (parseFloat(value) >= 6 ? Infinity : value)),
            itemStyle: {
              color: (item?.value?.every((v: string) => v === '') ? item?.itemStyle?.color : '#E24545'),
              borderColor: '#fff',
              borderWidth: 2
            },
            symbolSize: 12 // 设置点大小
          }
        ],
        lineStyle: {
          width: 0 // 不显示线
        },
      })) || [];

    return {
      legendData,
      indicator,
      seriesData,
      series: [
        ...(series||[]),
        ...redDotSeries,
        {
          name: "pie",
          type: "pie",
          z: 1,
          silent: true,
          data: pieData,
          animation: false,
          radius: ["0%", "65%"],
          // label: {
          //   distanceToLabelLine: -145,
          //   color: isDark ? "rgba(255, 255, 255, 0.3)" : "rgba(131, 134, 151, 0.6)",
          // },
          labelLine: {
            show: false,
          },
          // itemStyle: {
          //   borderWidth: 2,
          //   borderColor: isDark ? "none" : "#fff"
          // },
        },
      ],
    };
  }, [loading, isNodata, category, metric, performanceData, radarChartData, legend, versionList, isEN, pieLabelOffsetEn, pieLabelOffsetCn, isDark, metricIdList, legendNameMap, latestVersionObj?.version, checkColor, colorArrLess, colorArrMore, metricKey]);

  const formatterFunc = (params: any) => {
    // 雷达图指标解释浮窗
    if (params?.componentType === "radar" || metricKey) {
      const name = params?.componentType === "radar" ? params?.name : metricCategory?.find((v: { index: any; }) => v?.index === metricKey)?.metricName;
      let metricIndex = 0;
      let metric: any = {};
      metricCategory?.forEach((v: any, index: number) => {
        if (v?.metricName === name) {
          metric = { ...(v || {}) };
          metricIndex = index;
        }
      });
      const dom = <div className='dbd-echarts-tooltip-container dbd-echarts-radar-tooltip-gdtr'>
        <div className='dbd-echarts-radar-tooltip-content'>
          <div className='dbd-echarts-radar-tooltip-content-name'>{metric?.categoryName}</div>
          <div className='dbd-echarts-radar-tooltip-content-value'>{name}</div>
        </div>
        {
          seriesData?.map((item) => {
            // metricIndex是顺时针的下标，value是逆时针顺序需要反转
            const value = item?.value?.slice()?.reverse()?.[metricIndex];
            return <div className='dbd-echarts-radar-tooltip-content' key={item?.name}>
              <div className='dbd-echarts-radar-tooltip-content-name'>
                <span className='dbd-echarts-radar-tooltip-content-name-icon' style={{ backgroundColor: item?.itemStyle?.color }}></span>
                <span>{item?.name}</span>
              </div>
              <div className='dbd-echarts-radar-tooltip-content-value'>{value === "" ? "-" : value}</div>
            </div>;
          })
        }
      </div>;
      return ReactDOMServer.renderToString(dom);
    }
    // 雷达图游戏和top、median数据浮窗
    // const dom = <div className='dbd-echarts-tooltip-container dbd-echarts-radar-tooltip-gdtr'>
    //   <div>{params?.name}</div>
    //   {
    //     indicator?.slice()?.reverse()?.map((item: any, index: number) => {
    //       const value = params?.value?.slice()?.reverse()?.[index];
    //       return <div className='dbd-echarts-radar-tooltip-content' key={item?.key}>
    //         <div className='dbd-echarts-radar-tooltip-content-name'>
    //           <span className='dbd-echarts-radar-tooltip-content-name-icon' style={{ backgroundColor: params?.color }}></span>
    //           <span>{item?.name}</span>
    //         </div>
    //         <div className='dbd-echarts-radar-tooltip-content-value'>{value === "" ? "-" : value}</div>
    //       </div>;
    //     })
    //   }
    // </div>;
    // return ReactDOMServer.renderToString(dom);
  };

  const setOtherConfigFunc = useMemo(() => (opt: any) => {
    // 将 legendData 拆分成两部分展示
    const firstLineData = legendData.filter(item => priorityNames.includes(item.name));
    const secondLineData = legendData.filter(item => !priorityNames.includes(item.name));

    // 公共的 legend 配置
    const getCommonLegendConfig = (data: any[], bottom: string, type = 'plain', isFirstLine: boolean) => {
      return {
        type,
        orient: 'horizontal',
        data,
        bottom,
        icon: 'circle',
        itemHeight: 8,
        itemWidth: 8,
        selected,
        formatter: (name: string) => {
          const richObj = legendNameIconMap[name];

          let _name = '';
          if (isStudioChart) {
            _name = isFirstLine ? name : name.split('-').map(part => part.trim()).join('\n          ');
          } else {
            _name = name;
          }
          return richObj?.icon ? `{${richObj?.icon}| } ${_name}` : _name;
        },
        textStyle: {
          color: isDark ? "rgba(255, 255, 255, 0.6)" : '#262832',
          rich: {
            performance: {
              fontSize: 14,
              width: 34,
              height: 12,
              verticalAlign: "top",
              backgroundColor: {
                borderRadius: 4,
                image: LevelSvg,
              },
            },
            ...iconOption
          },
        },
        itemStyle: {
          borderWidth: isFirstLine ? checkBorderWidth : 0,
          borderColor: '#8B9AFF',
        }
      };
    };

    const radarAxisColor = isDark? "rgba(255, 255, 255, 0.9)" : "#141414";

    const options = {
      radar: {
        shape: "circle",
        startAngle: radarStartAngle,
        axisLine: {
          show: false,
        },
        radius: radarRadius,
        nameGap: radarLabelNameGap,
        splitArea: {
          show: false,
        },
        splitLine: {
          show: false,
        },
        axisName: {
          fontWeight: "500",
          fontSize: 14,
          backgroundColor: isDark ? "#464646" : "none",
          borderRadius: 30,
          padding: [5, 15, 5, 15],
          color: radarAxisColor,
          textBorderColor: radarAxisColor,
          textBorderWidth: isEN ? 0.2 : 0
        },
      },
      grid: {
        bottom: '100px', // Increase from 70px to 100px
      },
      legend: [
        getCommonLegendConfig(firstLineData, '35px', 'plain', true),
        getCommonLegendConfig(secondLineData, '-5px', 'scroll', false),
      ],
    };

    return merge({}, opt, options);
  }, [gameCover, legendNameIconMap, legendData, priorityNames, iconOption]);

  const chartEventConfig = (option: any, chart: any) => {
    if (!chart) return option;
    chart?.on('mouseover', (params: any) => {
      const metric = option.radar.indicator[params.event.topTarget.__dimIdx];
      setMetricKey(metric?.key);
    });
    chart?.on('legendselectchanged', (params: any) => {
      setSelected(params?.selected);
    });
    return option;
  };

  const fullscreenRootRef = useRef<HTMLDivElement>(null);
  const downloadRootRef = useRef<HTMLDivElement>(null);
  const radarChartRef = useRef<ChartRef>(null);
  const { isFullScreen, requestFullScreen, exitFullScreen } = useFullScreen(fullscreenRootRef, () => {
    // 切换全屏时自适应图表
    radarChartRef.current?.instance()?.resize();
  });

  const handleToggleFullscreen = () => {
    isFullScreen ? exitFullScreen() : requestFullScreen();
  };

  const handleDownloadPdf = async () => {
    setIsDownloading(true);

    const node = downloadRootRef.current;
    if (!node) {
      setIsDownloading(false);
      return;
    }

    // 获取实际的尺寸（不受 CSS zoom 影响）
    const rect = node.getBoundingClientRect();
    const computedStyle = getComputedStyle(node);
    const zoom = parseFloat(computedStyle.zoom || '1');
    
    // 计算未缩放的真实尺寸
    const actualWidth = rect.width / zoom;
    const actualHeight = rect.height / zoom;
    const extraBottomSpacing = isFullScreen ? 99 : 85;

    try {
      await captureNode({
        type: 'pdf',
        node,
        fileName: isEN ? 'GTDR Performance Overview' : 'GTDR 表现概览',
        nodeOptions: {
          width: actualWidth,
          height: actualHeight + extraBottomSpacing,
        },
      });
    } catch (error) {
      console.error(error);
    }
    finally {
      setIsDownloading(false);
    }
  };

  return <div className={classNames(className)} style={{ height: cardHeight, ...style}} >
    <Card className="register-table dbd-gtdr-radar-chart-card">
      <Card.Body>
        <Abnormal
          style={{ height: height ? height : 400 }}
          component={
            <div className='dbd-gtdr-radar-chart'>
              {showTitle && 
                <div className='dbd-radar-chart-title-container'>
                  <span className='dbd-radar-chart-title'>{title}</span>
                  <span className='dbd-radar-chart-subtitle'>{dynamicString(subtitle, data)}</span>
                </div>
              }
              <div ref={fullscreenRootRef} className={classNames('dbd-gtdr-radar-capture-root', {
                'dbd-gtdr-radar-capture-root-full-screen': isFullScreen,
              })}>
                <div className='dbd-gtdr-radar-capture-content'>
                  {!loading && !error && !(isNodata || !indicator || indicator.length === 0) ?
                    <div className={classNames('dbd-gtdr-radar-actions', CAPTURE_REMOVE_CLASS_NAME)}>
                      {showDownloadBtn && (
                        isDownloading ? (
                          <Loading text='' />
                        ) : (
                          <TipContext
                            tipText={formatMessage({id: 'dbl_common_download'})}
                            type={{ default: 'download', hover: 'download', }}
                            onClick={() => { void handleDownloadPdf() }}
                          /> 
                        )
                      )}
                      {showFullscreenBtn &&
                        <TipContext
                          tipText={formatMessage({id: 'dbl_common_fullscreen'})}
                          type={{ default: 'fullscreen', hover: 'fullscreen', }}
                          onClick={handleToggleFullscreen}
                        />}
                    </div> : <div></div>
                  }
                  <div className='dbd-radar-chart-color-map'>
                    {category?.map((item: any) => (
                      <div className='dbd-radar-chart-color-map-item' key={item}>
                        {/* <div className='dbd-radar-chart-color-map-color' style={{ backgroundColor: categoryColorArr?.[index] }}></div> */}
                        {/* <div className='dbd-radar-chart-color-map-name'>{item}</div> */}
                      </div>))
                    }
                  </div>
                  {/* 雷达图 */}
                  <div
                    ref={downloadRootRef}
                    className="dbd-gtdr-radar-chart-background-img"
                    style={{
                      '--background-image': `url(${backgroundImage})`, // 通过 CSS 变量传递背景图
                    }}
                  >
                    <RadarChart
                      ref={radarChartRef}
                      height={height}
                      legendData={legendData}
                      indicator={indicator}
                      series={series}
                      formatterFunc={formatterFunc}
                      chartEventConfig={chartEventConfig}
                      setOtherConfigFunc={setOtherConfigFunc}
                    />
                  </div>
                </div>
              </div>
              {/* Dimension 表格 */}
              {(showRiskLevel && riskColumns) &&
              <div  className={classNames('dbd-benchmark-gtdr-risk-level-card-container',className)}>
                <div className="dbd-benchmark-gtdr-risk-level-split-line"></div>
                <table className="dbd-benchmark-gtdr-risk-level-table">
                  <thead>
                    <tr>
                      {(riskColumns)?.map((item) => (
                        <th key={item.key} style={item.style}>
                          {item.header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(formattedRiskData)?.map((item: any) => {
                      const { category } = item;
                      return (
                        <tr key={category}>
                          {(riskColumns)?.map((col) => {
                            const { key, render } = col;
                            return (
                              <td key={key}>
                                {render ? render(item[col.key], item) : item[col.key]}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              }
            </div>
          }
          isLoading={loading}
          isError={error}
          isNoData={isNodata || !indicator || indicator.length === 0}
        />
      </Card.Body>
    </Card>
  </div>;
};

export default GTDRRadarChart;
